"use client";

/**
 * The nudge for a handle we do not have.
 *
 * A missing handle is not a profile being incomplete — it is a submission that
 * will fail much later and look like something else. The TikTok handle names
 * the Spark ads a video launches as, and the Instagram handle is the account
 * partnership requests are sent to, so an empty one is a launch that stops
 * without saying why.
 *
 * IT IS A ROW, not a notice with its own shape. It was built as one: a tinted
 * box with its own ring, its own padding and its own internal layout, sitting
 * in the page header. Three things were wrong with that and they were the same
 * thing. The ring is an edge on a pane, which the surface rules refuse
 * everywhere else. The header slot is 380 points wide, so two sentences wrapped
 * to four lines and the box read as cluttered while the page below it had a
 * full column going spare. And a second layout for what is plainly a row —
 * glyph, title, detail, chevron — meant the one thing on the page asking to be
 * pressed looked unlike everything else that could be.
 *
 * So it is `Row` on `CARD_ROW_WARN`, which is the destination card's own
 * recipe with a tone over the fill. Same padding, same radius, same press,
 * same opacity — the colour is the only difference, and it sits directly above
 * the cards it matches.
 *
 * It is not a DS `Alert` either: that primitive is a static notice with a
 * fixed light-mode palette, and this is a button on the candidate layer's
 * dark ground.
 */

import { AtSign } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { CARD_ROW_WARN, Row } from "./one/ui";

/**
 * Is there a handle to ask for?
 *
 * Exported because the caller has to know *before* it decides to render a
 * slot. A component that returns `null` still occupies its slot: the parent
 * holds an element, so `{notice ? <div>{notice}</div> : null}` is truthy and
 * lays out an empty wrapper — which in a column with a `section` gap costs two
 * gaps instead of one and put 96 points between Home's header and its cards.
 * The emptiness has to be decided where the slot is, not inside it.
 */
export function handlesNeedAttention(
  tiktokHandle: string | null,
  instagramHandle: string | null,
): boolean {
  return !tiktokHandle?.trim() || !instagramHandle?.trim();
}

export function HandlesPrompt({
  tiktokHandle,
  instagramHandle,
  onOpen,
  className,
}: {
  tiktokHandle: string | null;
  instagramHandle: string | null;
  onOpen: () => void;
  /** Placement only — where the caller needs it to sit. */
  className?: string;
}) {
  const t = useTranslations("home");
  const needsTiktok = !tiktokHandle?.trim();
  const needsInstagram = !instagramHandle?.trim();
  // Belt and braces: the caller gates on `handlesNeedAttention` so no empty
  // slot is laid out, and this keeps a caller that forgets from drawing an
  // empty prompt.
  if (!needsTiktok && !needsInstagram) return null;

  // Names the one that is missing rather than "your handles", so the sheet
  // does not open on two fields with no indication which of them we meant.
  const missing =
    needsTiktok && needsInstagram
      ? "both"
      : needsTiktok
        ? "tiktok"
        : "instagram";
  const title = t(`handles.prompt.${missing}.title`);
  const detail = t(`handles.prompt.${missing}.detail`);

  return (
    <Row
      className={cn(CARD_ROW_WARN, className)}
      icon={
        <AtSign
          className="size-[18px] text-nf-ds-tone-warn-fg"
          strokeWidth={2}
        />
      }
      title={title}
      detail={detail}
      onClick={onOpen}
    />
  );
}
